// Mock data for Cloud Accounting Support Services (CASS) clone
// NOTE: All data here is MOCKED for the frontend teaser. Will be replaced by backend APIs.

import {
  BookOpen, Calculator, Wallet, FileText, Receipt, TrendingUp, Building2,
  Cloud, GraduationCap, Wrench, ArrowRightLeft, Settings, LifeBuoy,
} from "lucide-react";
import { BUSINESS_HOURS_LABEL } from "./lib/business";

export const company = {
  name: "Cloud Accounting Support Services",
  short: "CASS",
  phone: "01827 780550",
  email: "info@cass-online.co.uk",
  hours: BUSINESS_HOURS_LABEL,
  address: "3 Pebble Close, Amington, Tamworth, Staffordshire B77 4RD",
  tagline: "Giving you more than numbers",
};

export const navServices = [
  { title: "Bookkeeping", slug: "bookkeeping", icon: BookOpen },
  { title: "Payroll", slug: "payroll", icon: Wallet },
  { title: "Ltd Co Accounts", slug: "ltd-co-accounts", icon: Building2 },
  { title: "Tax Planning", slug: "tax-planning", icon: TrendingUp },
  { title: "VAT", slug: "vat", icon: Receipt },
  { title: "Self Assessment", slug: "self-assessment", icon: FileText },
  { title: "MTD for IT", slug: "mtd-for-it", icon: Calculator },
  { title: "Xero Subscription", slug: "xero-subscription", icon: Cloud },
  { title: "Migration", slug: "migration", icon: ArrowRightLeft },
  { title: "Set up & Implementation", slug: "set-up-and-implementation", icon: Settings },
  { title: "Training", slug: "training", icon: GraduationCap },
  { title: "Troubleshooting", slug: "troubleshooting", icon: Wrench },
  { title: "Support", slug: "support", icon: LifeBuoy },
];

export const coreServices = [
  {
    title: "Bookkeeping", slug: "bookkeeping", icon: BookOpen,
    short: "Accurate, real-time bookkeeping that keeps your finances organised and compliant.",
    long: "Say goodbye to shoeboxes of receipts. We deliver accurate, cloud-based bookkeeping so you always know where your business stands. Real-time records, reconciled bank feeds and tidy ledgers ready for reporting and tax.",
    points: ["Bank reconciliation & feeds", "Purchase & sales ledgers", "Real-time cloud reporting", "VAT-ready records"],
  },
  {
    title: "MTD for IT", slug: "mtd-for-it", icon: Calculator,
    short: "Get ready for Making Tax Digital for Income Tax with confidence.",
    long: "Making Tax Digital for Income Tax is changing how sole traders and landlords report. We prepare your systems, keep you compliant with quarterly submissions and take the stress out of the transition.",
    points: ["Quarterly digital submissions", "Compliant software setup", "Sole trader & landlord ready", "Ongoing HMRC compliance"],
  },
  {
    title: "Payroll", slug: "payroll", icon: Wallet,
    short: "Reliable payroll processing that keeps your team paid and HMRC happy.",
    long: "From payslips to pensions and RTI submissions, our fully managed payroll service ensures your employees are paid correctly and on time, every time — with complete compliance.",
    points: ["RTI submissions to HMRC", "Auto-enrolment pensions", "Payslips & P60s", "Weekly or monthly runs"],
  },
  {
    title: "Self-Assessment", slug: "self-assessment", icon: FileText,
    short: "Stress-free self-assessment tax returns, filed accurately and on time.",
    long: "Whether you're a sole trader, landlord or director, we take the pain out of self-assessment. We calculate your liability, claim every allowance and file on time so you never miss a deadline.",
    points: ["Accurate tax calculations", "Allowances & reliefs claimed", "On-time HMRC filing", "Deadline reminders"],
  },
  {
    title: "VAT", slug: "vat", icon: Receipt,
    short: "MTD-compliant VAT returns prepared and submitted with precision.",
    long: "VAT can be complex. We handle registration, scheme selection and MTD-compliant returns so you stay on the right side of HMRC while optimising your position.",
    points: ["VAT registration", "MTD-compliant returns", "Scheme advice (flat rate, cash)", "Timely submissions"],
  },
  {
    title: "Tax Planning", slug: "tax-planning", icon: TrendingUp,
    short: "Proactive tax planning to keep more of what you earn — legally.",
    long: "Good tax planning is about looking ahead. We work with you throughout the year to structure your affairs efficiently, maximise reliefs and minimise your liability within the rules.",
    points: ["Year-round planning", "Profit extraction strategies", "Reliefs & allowances", "Business structure advice"],
  },
  {
    title: "Ltd Co Accounts", slug: "ltd-co-accounts", icon: Building2,
    short: "Full statutory accounts and corporation tax for limited companies.",
    long: "We prepare and file your year-end statutory accounts and corporation tax returns with Companies House and HMRC — accurate, compliant and delivered on time.",
    points: ["Statutory year-end accounts", "Corporation tax returns", "Companies House filing", "Directors' reporting"],
  },
];

export const softwareServices = [
  {
    title: "Xero", slug: "xero-subscription", icon: Cloud,
    short: "As a Xero Platinum Partner we set you up on the world's leading cloud accounting platform.",
    long: "As a Xero Platinum Partner Firm, we help you get the most from Xero — from subscriptions and add-ons to bespoke workflows tailored to your business.",
    points: ["Discounted Xero subscriptions", "App ecosystem advice", "Tailored workflows", "Platinum Partner expertise"],
  },
  {
    title: "Training", slug: "training", icon: GraduationCap,
    short: "One-to-one and team Xero training so you feel confident in your numbers.",
    long: "Empower yourself and your team with hands-on Xero training. We tailor sessions to your business and skill level so you get real value from your software.",
    points: ["One-to-one sessions", "Team workshops", "Tailored to your business", "Follow-up support"],
  },
  {
    title: "Troubleshooting", slug: "troubleshooting", icon: Wrench,
    short: "Stuck? We diagnose and fix your accounting software issues fast.",
    long: "When something isn't right in your accounts, we get to the bottom of it quickly — fixing reconciliation issues, migrations gone wrong and configuration problems.",
    points: ["Reconciliation fixes", "Data clean-up", "Configuration review", "Rapid resolution"],
  },
  {
    title: "Migration", slug: "migration", icon: ArrowRightLeft,
    short: "Seamless migration from your old system to Xero with zero data loss.",
    long: "Moving to Xero? We handle the whole migration — mapping your data, preserving history and getting you live with minimal disruption.",
    points: ["Data mapping & transfer", "Historical data preserved", "Minimal downtime", "Post-migration checks"],
  },
  {
    title: "Support", slug: "support", icon: LifeBuoy,
    short: "Ongoing support whenever you need a friendly expert in your corner.",
    long: "Accounting shouldn't be a once-a-year conversation. Our support keeps you moving with quick answers, proactive advice and a real person who knows your business.",
    points: ["Responsive help desk", "Proactive advice", "Regular check-ins", "A real person, not a bot"],
  },
  {
    title: "Set up & Implementation", slug: "set-up-and-implementation", icon: Settings,
    short: "We design and implement the perfect cloud accounting stack for you.",
    long: "From chart of accounts to bank feeds and connected apps, we set up your cloud accounting from the ground up so it works the way your business does.",
    points: ["Chart of accounts design", "Bank feed connections", "App integrations", "Best-practice setup"],
  },
];

export const allServices = [...coreServices, ...softwareServices];

export const partnerBadges = [
  { label: "Xero Platinum Partner", tag: "PLATINUM" },
  { label: "Certified Advisor", tag: "CERTIFIED" },
  { label: "MTD Ready", tag: "MTD" },
  { label: "ICB Regulated", tag: "ICB" },
];

export const stats = [
  { value: "15+", label: "Years of cloud experience" },
  { value: "150+", label: "Businesses supported" },
  { value: "Platinum", label: "Xero Partner status" },
  { value: "4.9/5", label: "Average client rating" },
];

export const testimonials = [
  {
    id: 1, name: "Sarah Mitchell", role: "Founder, Bloom Interiors",
    quote: "CASS transformed the way we handle our finances. Moving to Xero was seamless and I finally understand my numbers. They truly give you more than numbers.",
    rating: 5,
  },
  {
    id: 2, name: "James Thornton", role: "Director, Thornton Building Ltd",
    quote: "The team took the stress out of our year-end accounts and VAT. Responsive, friendly and genuinely knowledgeable. Couldn't recommend them more highly.",
    rating: 5,
  },
  {
    id: 3, name: "Priya Sharma", role: "Owner, Grace Cares",
    quote: "They support us AND our accountant. Nothing is ever too much trouble and the training gave my team real confidence with our bookkeeping.",
    rating: 5,
  },
  {
    id: 4, name: "David Okafor", role: "Sole Trader, DO Consulting",
    quote: "Getting ready for Making Tax Digital felt daunting until CASS stepped in. Clear advice, proper setup and total peace of mind.",
    rating: 5,
  },
];

export const videoTestimonials = [
  {
    id: 1, title: "We're good at what we do… find out here!",
    youtubeId: "dQw4w9WgXcQ",
    thumbnail: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
  {
    id: 2, title: "Grace Cares – Supporting them and their accountant",
    youtubeId: "dQw4w9WgXcQ",
    thumbnail: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  },
];

export const blogPosts = [
  {
    id: 1, slug: "making-tax-digital-income-tax-2026",
    title: "Making Tax Digital for Income Tax: What SMEs Need to Know in 2026",
    category: "MTD",
    excerpt: "MTD for Income Tax is on the horizon. Here's a practical breakdown of who's affected, key dates and how to prepare without the panic.",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    author: "The CASS Team", date: "2026-06-18", readTime: "6 min read",
    content: [
      "Making Tax Digital (MTD) for Income Tax is one of the biggest changes to hit self-employed people and landlords in years. From the mandation date, affected taxpayers will need to keep digital records and submit quarterly updates to HMRC using compatible software.",
      "The good news? With the right cloud accounting setup, MTD becomes routine rather than a burden. Digital bank feeds and automated categorisation mean your records stay up to date throughout the year.",
      "If your qualifying income is above the threshold, now is the time to get your systems in order. We help clients choose the right software, set up their books and stay comfortably compliant.",
      "Speak to us about a free review of your current setup and we'll map out exactly what you need to do before the deadline.",
    ],
  },
  {
    id: 2, slug: "5-tax-planning-tips-small-business",
    title: "5 Tax Planning Tips Every Small Business Should Act On",
    category: "Tax Planning",
    excerpt: "Simple, legitimate strategies to reduce your tax bill and keep more of your hard-earned profit this financial year.",
    image: "https://images.unsplash.com/photo-1554224154-22dec7ec8818?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    author: "The CASS Team", date: "2026-05-30", readTime: "5 min read",
    content: [
      "Effective tax planning isn't about last-minute scrambling — it's about making smart, proactive decisions throughout the year. Here are five areas where small businesses often leave money on the table.",
      "1. Profit extraction: The mix of salary and dividends can make a real difference to a director's take-home pay. 2. Pension contributions: A tax-efficient way to reward yourself. 3. Capital allowances: Claim for equipment and qualifying assets. 4. Timing income and expenses: Small adjustments can shift liabilities favourably. 5. Using allowances: Don't waste your annual exemptions.",
      "Every business is different, so the right strategy depends on your circumstances. Let's talk about your business and we'll show you where the opportunities are.",
    ],
  },
  {
    id: 3, slug: "why-move-to-xero-cloud-accounting",
    title: "Why Moving to Xero Could Be the Best Decision for Your Business",
    category: "Software",
    excerpt: "Cloud accounting isn't just a trend — it's a smarter way to run your finances. Here's what Xero can do for you.",
    image: "https://images.unsplash.com/photo-1628348068343-c6a848d2b6dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    author: "The CASS Team", date: "2026-05-12", readTime: "4 min read",
    content: [
      "As a Xero Platinum Partner Firm, we've helped hundreds of businesses move to the cloud — and the transformation is consistent: better visibility, less admin and smarter decisions.",
      "With live bank feeds, automated invoicing and real-time dashboards, you always know where your business stands. No more waiting until year-end to understand your numbers.",
      "Migration sounds scary, but with our proven process it's smooth and low-risk. We map your data, preserve your history and get you live with minimal disruption.",
    ],
  },
  {
    id: 4, slug: "vat-schemes-explained",
    title: "VAT Schemes Explained: Which One Is Right for You?",
    category: "VAT",
    excerpt: "Flat rate, cash accounting or standard? Understanding VAT schemes could save your business time and money.",
    image: "https://images.pexels.com/photos/7821551/pexels-photo-7821551.jpeg?auto=compress&cs=tinysrgb&w=800",
    author: "The CASS Team", date: "2026-04-22", readTime: "5 min read",
    content: [
      "Choosing the right VAT scheme can have a surprising impact on your cash flow and admin. The standard scheme suits many, but the flat rate and cash accounting schemes can offer real advantages for smaller businesses.",
      "The Flat Rate Scheme simplifies your VAT by applying a fixed percentage to your turnover. Cash Accounting means you only pay VAT once you've been paid — a lifesaver for businesses with slow-paying customers.",
      "We assess your situation and recommend the scheme that works best, then handle MTD-compliant submissions on your behalf.",
    ],
  },
  {
    id: 5, slug: "budget-implications-for-smes",
    title: "The Budget & Your Business: Practical Strategies for SMEs",
    category: "Business",
    excerpt: "Key takeaways from the latest Budget and what they mean for small and medium-sized businesses across the UK.",
    image: "https://images.unsplash.com/photo-1444653614773-995cb1ef9efa?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    author: "The CASS Team", date: "2026-04-05", readTime: "7 min read",
    content: [
      "Every Budget brings changes that ripple through to small businesses — from tax thresholds to allowances and reliefs. Understanding the implications early lets you plan rather than react.",
      "In this roundtable summary, leading accounting and finance professionals share practical strategies for navigating a changing financial landscape.",
      "Download our free Business Owner's Guide to the Budget Implications for the full breakdown and actionable steps.",
    ],
  },
  {
    id: 6, slug: "bookkeeping-mistakes-to-avoid",
    title: "7 Common Bookkeeping Mistakes (and How to Avoid Them)",
    category: "Bookkeeping",
    excerpt: "Small errors in your books can lead to big headaches. Here are the most common pitfalls we see — and how to steer clear.",
    image: "https://images.pexels.com/photos/7821544/pexels-photo-7821544.jpeg?auto=compress&cs=tinysrgb&w=800",
    author: "The CASS Team", date: "2026-03-19", readTime: "5 min read",
    content: [
      "Good bookkeeping is the foundation of a healthy business. Yet we regularly see the same avoidable mistakes: mixing personal and business expenses, falling behind on reconciliation, and losing receipts.",
      "Cloud accounting solves most of these automatically — receipt capture apps, live bank feeds and clear categorisation keep everything tidy and audit-ready.",
      "If your books have got a little messy, don't worry. We offer a clean-up service to get you back on track.",
    ],
  },
];

export const podcasts = [
  {
    id: 1, slug: "more-than-numbers-ep1",
    title: "More Than Numbers — Episode 1: Starting Your Business Right",
    episode: "Episode 1",
    description: "We kick off the series with the financial foundations every new business owner should get right from day one.",
    image: "https://images.unsplash.com/photo-1589903308904-1010c2294adc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    duration: "34 min", date: "2026-06-01", youtubeId: "dQw4w9WgXcQ",
  },
  {
    id: 2, slug: "more-than-numbers-ep2",
    title: "More Than Numbers — Episode 2: Demystifying Making Tax Digital",
    episode: "Episode 2",
    description: "Everything sole traders and landlords need to know about MTD for Income Tax, explained in plain English.",
    image: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    duration: "41 min", date: "2026-05-15", youtubeId: "dQw4w9WgXcQ",
  },
  {
    id: 3, slug: "more-than-numbers-ep3",
    title: "More Than Numbers — Episode 3: Cash Flow Is King",
    episode: "Episode 3",
    description: "Practical tips for managing cash flow, chasing invoices and keeping your business financially healthy.",
    image: "https://images.pexels.com/photos/31236103/pexels-photo-31236103.jpeg?auto=compress&cs=tinysrgb&w=800",
    duration: "29 min", date: "2026-04-28", youtubeId: "dQw4w9WgXcQ",
  },
];

export const downloads = [
  {
    id: 1, title: "The Ultimate Guide to Choosing Accounting Software",
    description: "With so many accounting software platforms things can get overwhelming very quickly. Download our free E-Book to help find the right product for you.",
  },
  {
    id: 2, title: "Business Owner's Guide to the Budget Implications",
    description: "Practical strategies for SMEs in a changing financial landscape, from a roundtable discussion with leading accounting and finance professionals.",
  },
];

export const aboutContent = {
  intro: "At CASS, we believe great accounting is about more than keeping you compliant. It's about giving you the clarity, confidence and support to make better financial decisions. As a Xero Platinum Partner, we combine modern cloud technology with genuine relationships and proactive advice to help your business grow with confidence.",
  mission: "We exist to help business owners sleep better at night by giving them confidence in their finances and a trusted partner to help them grow.",
  values: [
    { title: "Relationships First", icon: "Handshake", text: "We build long-term partnerships based on trust, honesty and genuine care for our clients." },
    { title: "Proactive Advice", icon: "Lightbulb", text: "We don't wait for problems to arise. We help you plan ahead and make better decisions." },
    { title: "Modern Technology", icon: "Cloud", text: "As Xero specialists, we use cloud technology to make accounting simpler, faster and more valuable." },
    { title: "Straightforward Communication", icon: "MessageSquare", text: "No jargon. No unnecessary complexity. Just practical advice you can understand and act on." },
    { title: "Helping Businesses Grow", icon: "TrendingUp", text: "Everything we do is focused on helping our clients gain clarity, confidence and control over their finances." },
    { title: "Confidence & Peace of Mind", icon: "ShieldCheck", text: "Giving you confidence that your finances are in safe hands, so you can focus on running your business." },
  ],
  images: [
    "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  ],
};
